import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";

import ScrollToTop from "../../../components/utils/ScrollToTop";
import { Button } from "../../../probate/components/ui/button";
import { request } from "../../../utils/apiClient";
import { useSelectedCase } from "../utils/hooks";
import { TaskError } from "./ProbateDoable";
import ProbatePage from "./ProbatePage";
import { SheetBody } from "./Sheet";

const requestSignInLink = (emailAddress) =>
  request("/api/login", {
    method: "POST",
    body: JSON.stringify({ email_address: emailAddress }),
    credentials: "same-origin",
    sentryLabel: "ResendSignInLink",
  });

const ProbateConfirmEmail = () => {
  const session = useSelector((state) => state.dashboardState.session);
  const selectedCase = useSelectedCase({ caseScreen: "case" });
  const [status, setStatus] = useState("idle");
  const sentMessage = useRef(null);

  // The button unmounts, so focus would fall to the top of the page
  useEffect(() => {
    if (status === "sent") sentMessage.current?.focus();
  }, [status]);

  if (!selectedCase) {
    return null;
  }

  // Absent for staff sessions
  if (session.email_verified !== false) {
    return <Navigate to={`/dashboard/${selectedCase.case_reference}`} replace />;
  }

  const emailAddress = session.email_address;

  const resend = async () => {
    setStatus("sending");
    try {
      await requestSignInLink(emailAddress);
      setStatus("sent");
    } catch {
      setStatus("failed");
    }
  };

  return (
    <ProbatePage
      caseRef={selectedCase.case_reference}
      title="Confirm your email address"
      details={`We sent a link to ${emailAddress}.`}
    >
      <ScrollToTop />
      <SheetBody>
        {status === "sent" ? (
          <p role="status" tabIndex={-1} ref={sentMessage}>
            We’ve sent a new link to {emailAddress}.
          </p>
        ) : (
          <>
            {status === "failed" && <TaskError role="alert">We couldn’t send a new link. Please try again.</TaskError>}
            <Button variant="secondary" onClick={resend} disabled={status === "sending"} focusableWhenDisabled>
              Send a new link
            </Button>
          </>
        )}
      </SheetBody>
    </ProbatePage>
  );
};

export default ProbateConfirmEmail;
